/* eslint-disable react/prop-types */
import { forwardRef, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Share2, Phone, MapPin, Mail, ArrowLeft, Globe as Website } from 'lucide-react';
import CategoryTree from '@/components/common/Categorytree';
import { useSwipeable } from 'react-swipeable';
import BottomNav from '@/components/layouts/Footer/BottomNav';
import formatAddress from '@/utils/formatAddress';
import BusinessAttributes from '@/components/common/BusinessAttributes';
import { SWIPE_DIRECTIONS, URLS } from '../constant/constant';
import ImageModal from '../components/modals/ImageModal';
import { useUser } from '../contexts/UserContext';

// ====================================================================================
// Brand SVG Icons
// ====================================================================================
const YouTubeIcon = () => (
  <svg viewBox="0 0 24 24" className="size-4" fill="currentColor">
    <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
  </svg>
);

const FacebookIcon = () => (
  <svg viewBox="0 0 24 24" className="size-4" fill="currentColor">
    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
  </svg>
);

const InstagramIcon = () => (
  <svg viewBox="0 0 24 24" className="size-4" fill="currentColor">
    <path d="M12 0C8.74 0 8.333.015 7.053.072 5.775.132 4.905.333 4.14.63c-.789.306-1.459.717-2.126 1.384S.935 3.35.63 4.14C.333 4.905.131 5.775.072 7.053.012 8.333 0 8.74 0 12s.015 3.667.072 4.947c.06 1.277.261 2.148.558 2.913.306.788.717 1.459 1.384 2.126.667.666 1.336 1.079 2.126 1.384.766.296 1.636.499 2.913.558C8.333 23.988 8.74 24 12 24s3.667-.015 4.947-.072c1.277-.06 2.148-.262 2.913-.558.788-.306 1.459-.718 2.126-1.384.666-.667 1.079-1.335 1.384-2.126.296-.765.499-1.636.558-2.913.06-1.28.072-1.687.072-4.947s-.015-3.667-.072-4.947c-.06-1.277-.262-2.149-.558-2.913-.306-.789-.718-1.459-1.384-2.126C21.319 1.347 20.651.935 19.86.63c-.765-.297-1.636-.499-2.913-.558C15.667.012 15.26 0 12 0zm0 2.16c3.203 0 3.585.016 4.85.071 1.17.055 1.805.249 2.227.415.562.217.96.477 1.382.896.419.42.679.819.896 1.381.164.422.36 1.057.413 2.227.057 1.266.07 1.646.07 4.85s-.015 3.585-.074 4.85c-.061 1.17-.256 1.805-.421 2.227-.224.562-.479.96-.899 1.382-.419.419-.824.679-1.38.896-.42.164-1.065.36-2.235.413-1.274.057-1.649.07-4.859.07-3.211 0-3.586-.015-4.859-.074-1.171-.061-1.816-.256-2.236-.421-.569-.224-.96-.479-1.379-.899-.421-.419-.69-.824-.9-1.38-.165-.42-.359-1.065-.42-2.235-.045-1.26-.061-1.649-.061-4.844 0-3.196.016-3.586.061-4.861.061-1.17.255-1.814.42-2.234.21-.57.479-.96.9-1.381.419-.419.81-.689 1.379-.898.42-.166 1.051-.361 2.221-.421 1.275-.045 1.65-.06 4.859-.06l.045.03zm0 3.678c-3.405 0-6.162 2.76-6.162 6.162 0 3.405 2.76 6.162 6.162 6.162 3.405 0 6.162-2.76 6.162-6.162 0-3.405-2.76-6.162-6.162-6.162zM12 16c-2.21 0-4-1.79-4-4s1.79-4 4-4 4 1.79 4 4-1.79 4-4 4zm7.846-10.405c0 .795-.646 1.44-1.44 1.44-.795 0-1.44-.646-1.44-1.44 0-.794.646-1.439 1.44-1.439.793-.001 1.44.645 1.44 1.439z" />
  </svg>
);

const XIcon = () => (
  <svg viewBox="0 0 24 24" className="size-4" fill="currentColor">
    <path d="M18.901 1.153h3.68l-8.04 9.19L24 22.846h-7.406l-5.8-7.584-6.638 7.584H.474l8.6-9.83L0 1.154h7.594l5.243 6.932ZM17.61 20.644h2.039L6.486 3.24H4.298Z" />
  </svg>
);

const WhatsAppIcon = () => (
  <svg viewBox="0 0 24 24" className="size-4" fill="currentColor">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" />
  </svg>
);

const WebsiteIcon = Website;

// ====================================================================================
// Icon Map for Social Media
// ====================================================================================
const IconMap = {
  youtube: {
    component: YouTubeIcon,
    color: 'text-red-600',
    bgColor: 'bg-red-100',
  },
  facebook: {
    component: FacebookIcon,
    color: 'text-blue-600',
    bgColor: 'bg-blue-100',
  },
  instagram: {
    component: InstagramIcon,
    color: 'text-pink-600',
    bgColor: 'bg-pink-100',
  },
  Twitter: {
    component: XIcon,
    color: 'text-black',
    bgColor: 'bg-gray-300',
  },
  website: {
    component: WebsiteIcon,
    color: 'text-blue-600',
    bgColor: 'bg-blue-100',
  },
  whatsapp: {
    component: WhatsAppIcon,
    color: 'text-green-600',
    bgColor: 'bg-green-100',
  },
};

// ====================================================================================
// Data Helper Functions
// ====================================================================================
function getAboutInfo(vendor, field) {
  const attr = vendor?.attributeValues?.find((attr) => attr?.name === field);
  return attr?.value;
}

function getSocialMediaLinks(vendor) {
  const labels = ['facebook', 'youtube', 'instagram', 'Twitter', 'website'];
  const socialMediaLinks = vendor?.attributeValues?.filter((attr) => {
    if (labels.includes(attr?.name) && attr?.value) {
      return true;
    }
    return false;
  });
  return socialMediaLinks || [];
}

function getProfileImage(v) {
  return v?.profileImage || '/images/imagenotfound.webp';
}

function getCoverImage(v) {
  return v?.coverImage || '/images/imagenotfound.webp';
}

function getImages(v) {
  return Array.isArray(v?.vendorImages) && v.vendorImages.length > 0 ? v.vendorImages : [];
}

function getAttrMap(v) {
  const map = {};
  const list = Array.isArray(v?.attributeValues) ? v.attributeValues : [];
  list.forEach((it) => {
    const key = (it?.attributeName || it?.name || '').toString().trim();
    const val = it?.attributeValue ?? it?.value;
    if (key) map[key] = val;
  });
  return map;
}

function SkeletonCard() {
  return (
    <div className="mb-4 break-inside-avoid">
      <div className="w-full h-48 bg-slate-200 rounded-lg animate-pulse"></div>
    </div>
  );
}

// ====================================================================================
// Main Page Component: ShopProfilePage
// ====================================================================================
export default function ShopProfilePage() {
  const navigate = useNavigate();
  const { user, userLoading } = useUser();

  const [activeTab, setActiveTab] = useState('overview');
  const [isLoading, setIsLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState(null);
  const tabRefs = useRef({});

  // Use vendor data directly from user context
  const vendorData = user;

  const vendorInfo = useMemo(() => {
    if (!vendorData) return null;
    const attr = getAttrMap(vendorData);

    return {
      shopName: vendorData?.shopName,
      isShowcaseOnly: !!vendorData?.viewOnly && !vendorData?.storeFront,
      name: vendorData?.name,
      subtitle: getAboutInfo(vendorData, 'tagline'),
      profileImg: getProfileImage(vendorData),
      coverImg: getCoverImage(vendorData),
      images: getImages(vendorData),
      addressStr: formatAddress(vendorData?.address),
      phone: vendorData?.contactNo,
      alternatePhone: vendorData?.alternateContact,
      email: vendorData?.emailId,
      about: getAboutInfo(vendorData, 'aboutUs') || '',
      attrMap: attr,
      socialMedia: getSocialMediaLinks(vendorData),
    };
  }, [vendorData]);

  const tabs = useMemo(() => {
    if (!vendorInfo) return [];
    const hasEventDetails =
      vendorInfo?.attrMap?.eventDetails && vendorInfo.attrMap.eventDetails.trim() !== '';
    const hasAnnouncements =
      vendorInfo?.attrMap?.announcement && vendorInfo.attrMap.announcement.trim() !== '';

    return [
      { id: 'overview', label: 'Overview' },
      hasEventDetails ? { id: 'event', label: 'Event' } : null,
      hasAnnouncements ? { id: 'announcement', label: 'Announcement' } : null,
      { id: 'categories', label: vendorInfo?.isShowcaseOnly ? 'Services' : 'Categories' },
      { id: 'gallery', label: 'Gallery' },
      { id: 'business-info', label: 'Business Info' },
    ].filter(Boolean);
  }, [vendorInfo]);

  const handlers = useSwipeable({
    onSwiped: (eventData) => {
      const currTab = activeTab;
      const idx = tabs.findIndex((i) => i.id === currTab);
      if (eventData?.dir === SWIPE_DIRECTIONS.RIGHT && idx > 0) {
        setActiveTab(tabs[idx - 1].id);
      } else if (eventData?.dir === SWIPE_DIRECTIONS.LEFT && idx < tabs?.length - 1) {
        setActiveTab(tabs[idx + 1].id);
      }
    },
  });

  useEffect(() => {
    window.scrollTo(0, 0);
    const activeTabElement = tabRefs.current[activeTab];
    if (activeTabElement) {
      activeTabElement.scrollIntoView({
        behavior: 'smooth',
        inline: 'center',
        block: 'nearest',
      });
    }
  }, [activeTab]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 1000);
    return () => clearTimeout(timer);
  }, []);

  // Show loading state while user data is being fetched
  if (userLoading) {
    return (
      <main className="flex h-screen items-center justify-center bg-slate-50">
        <div className="rounded-lg border bg-white p-8 text-center shadow-sm">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600 mx-auto"></div>
          <p className="mt-4 text-lg text-slate-600">Loading vendor profile...</p>
        </div>
      </main>
    );
  }

  if (!vendorInfo) {
    return (
      <main className="flex h-screen items-center justify-center bg-slate-50">
        <div className="rounded-lg border bg-white p-8 text-center shadow-sm">
          <p className="text-lg text-slate-600">Vendor data not found.</p>
          <button
            onClick={() => navigate(-1)}
            className="mt-6 rounded-md bg-emerald-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-emerald-500"
          >
            Go Back
          </button>
        </div>
      </main>
    );
  }

  const hasEventDetails =
    vendorInfo?.attrMap?.eventDetails && vendorInfo.attrMap.eventDetails.trim() !== '';

  return (
    <div className="min-h-screen pb-10 bg-slate-50 text-slate-800">
      <ProfileHeader vendorInfo={vendorInfo} vendorData={vendorData} />
      {/* Tab Navigation */}
      <nav className="border-b md:mt-4 border-slate-200">
        <div className="-mb-px flex space-x-4 overflow-x-auto">
          {tabs.map((tab) => (
            <TabButton
              key={tab.id}
              id={tab.id}
              ref={(el) => (tabRefs.current[tab.id] = el)}
              activeTab={activeTab}
              setActiveTab={setActiveTab}
              label={tab.label}
            />
          ))}
        </div>
      </nav>
      {/* Tab Content Panels */}
      <div {...handlers} className="md:pb-2 px-2 pb-12">
        {activeTab === 'overview' && <OverviewPanel info={vendorInfo} />}
        {activeTab === 'event' && <EventPanel info={vendorInfo} />}
        {activeTab === 'announcement' && <AnnouncementPanel info={vendorInfo} />}
        {activeTab === 'gallery' && (
          <GalleryPanel
            isLoading={isLoading}
            images={vendorInfo.images}
            onImageClick={setSelectedImage}
          />
        )}
        {activeTab === 'categories' && (
          <CategoryTree
            isShowcaseOnly={vendorInfo?.isShowcaseOnly}
            categories={vendorData?.vendorCategories || []}
          />
        )}
        {activeTab === 'business-info' && (
          <BusinessAttributes
            attributeValues={vendorData?.attributeValues || []}
            attr={vendorInfo.attrMap}
          />
        )}
      </div>
      {selectedImage && (
        <ImageModal imageUrl={selectedImage} onClose={() => setSelectedImage(null)} />
      )}
      <BottomNav />
    </div>
  );
}

// ====================================================================================
// Sub-components
// ====================================================================================
function ProfileHeader({ vendorInfo, vendorData }) {
  const navigate = useNavigate();
  const { user } = useUser();

  const handleShare = async (e) => {
    e?.stopPropagation();
    const inviteCode = vendorInfo.phone?.replace('+91', '') || '';
    const url = `${URLS.USER}/vendor/profile?mobile=${inviteCode}`;
    const shopName = vendorInfo.shopName || `${vendorInfo?.name}'s Store`;
    const title = `${shopName} on Biglocal`;
    const text = `${shopName} on Biglocal \nDiscover *${shopName}'s* product and services now on Biglocal.in! We are your local seller, online and ready to serve you. Support neighborhood businesses and *Go Local—it makes a difference!*\n👇 Tap below to explore & order:\n${url}`;

    if (navigator.share) {
      try {
        await navigator.share({ title, text });
      } catch (err) {
        if (err.name !== 'AbortError') {
          console.error(err);
        }
      }
    } else {
      try {
        await navigator.clipboard.writeText(`${title}\n\n${text}`);
        alert('Shop link copied! Share it with your friends.');
      } catch (err) {
        console.error('Clipboard copy failed:', err);
      }
    }
  };

  const eventBanner = vendorInfo?.attrMap?.eventBanner;
  const hasEventBanner = eventBanner && eventBanner.trim() !== '';
  const bannerImage = hasEventBanner ? eventBanner : '';
  const hasEventDetails =
    vendorInfo?.attrMap?.eventDetails && vendorInfo.attrMap.eventDetails.trim() !== '';

  return (
    <section className="relative">
      {/* Desktop Event Banner Layout */}
      <div className="hidden lg:block">
        <div className="bg-emerald-800 min-h-96 px-4 pt-4 pb-28">
          <div className="flex absolute right-0 left-0 px-3 w-full justify-between items-center mb-4">
            <div onClick={() => navigate(-1)} className="flex gap-3 items-center cursor-pointer">
              <button className="grid size-8 place-items-center rounded-full bg-white/20 backdrop-blur-sm text-white hover:bg-white/30 transition-colors">
                <ArrowLeft className="size-4" />
              </button>
              <div className="flex gap-1 items-center text-sm font-bold text-white/80">
                <p>Home</p>
                <p>/</p>
                <p>Profile</p>
              </div>
            </div>
          </div>
          <h1 className="text-3xl md:text-4xl font-bold text-white text-center">
            {vendorInfo.shopName}
          </h1>
          {vendorInfo.subtitle && (
            <p className="text-white/80 text-center mt-2">{vendorInfo.subtitle}</p>
          )}
          {/* Social Media Links in header */}
          <div className="flex items-center justify-center gap-3 mt-4">
            {vendorInfo.phone && (
              <button
                onClick={() => {
                  const phoneNumber = vendorInfo.phone?.replace(/[^0-9]/g, '');
                  window.open(`https://wa.me/${phoneNumber}`, '_blank');
                }}
                className="grid size-9 place-items-center rounded-full bg-[#25D366] text-white hover:bg-[#128C7E] transition-colors"
                title="WhatsApp"
              >
                <WhatsAppIcon />
              </button>
            )}
            {vendorInfo.addressStr && (
              <button
                onClick={() => {
                  const q =
                    vendorInfo?.attrMap?.latitude && vendorInfo?.attrMap?.longitude
                      ? `${vendorInfo.attrMap.latitude},${vendorInfo.attrMap.longitude}`
                      : vendorInfo?.addressStr;
                  if (q) {
                    const encodedQuery = encodeURIComponent(q);
                    const mapsUrl = `https://www.google.com/maps/search/?api=1&query=${encodedQuery}`;
                    window.open(mapsUrl, '_blank', 'noopener,noreferrer');
                  }
                }}
                className="grid size-9 place-items-center rounded-full bg-[#EA4335] text-white hover:bg-[#C5221F] transition-colors"
                title="Open in Maps"
              >
                <MapPin className="size-4" />
              </button>
            )}
            {vendorInfo?.socialMedia?.map((link) => {
              const iconConfig = IconMap[link?.name];
              if (!iconConfig) return null;
              const IconComponent = iconConfig.component;
              const brandColors = {
                youtube: 'bg-[#FF0000] hover:bg-[#CC0000]',
                facebook: 'bg-[#1877F2] hover:bg-[#0d65d9]',
                instagram:
                  'bg-gradient-to-br from-[#833AB4] via-[#FD1D1D] to-[#F77737] hover:opacity-90',
                Twitter: 'bg-black hover:bg-gray-800',
                website: 'bg-[#4285F4] hover:bg-[#3367D6]',
              };
              const colorClass = brandColors[link?.name] || 'bg-white/20 hover:bg-white/30';
              return (
                <button
                  key={link?.id}
                  onClick={() => window.open(link?.value, '_blank')}
                  className={`grid size-9 place-items-center rounded-full text-white transition-colors ${colorClass}`}
                  title={link?.name}
                >
                  {typeof IconComponent === 'function' ? (
                    <IconComponent />
                  ) : (
                    <IconComponent className="size-4" />
                  )}
                </button>
              );
            })}
          </div>
        </div>
        {vendorInfo?.profileImage && (
          <img
            src={vendorInfo?.profileImg || '/images/imagenotfound.webp'}
            alt="Profile"
            className="rounded-full size-32 md:block hidden -mt-20 absolute left-28 object-cover border-4 border-white"
            onError={(e) => {
              e.target.src = '/images/imagenotfound.webp';
            }}
          />
        )}
        <div className="bg-slate-100 px-6 py-8 flex justify-center">
          <div className="max-w-xl -mt-60 w-full">
            <img
              loading="lazy"
              onClick={() => navigate('/store')}
              src={bannerImage || '/images/bannernotfound.jpeg'}
              alt={`${vendorInfo.shopName} banner`}
              className="w-full cursor-pointer h-auto rounded-lg shadow-lg object-contain"
              onError={(e) => {
                e.target.src = '/images/imagenotfound.webp';
              }}
            />
            <div className="flex justify-center mt-4 items-center gap-2">
              {!vendorInfo?.isShowcaseOnly && (
                <button
                  onClick={() => {
                    console.log(`${URLS.USER}/vendor/profile?mobile=${vendorInfo?.contactNo}`);
                    window.open(`${URLS.USER}/vendor/profile?mobile=${vendorInfo?.contactNo}`);
                  }}
                  className="px-4 py-1.5 bg-orange-600 text-white rounded-md font-semibold text-sm hover:bg-orange-700 transition-colors"
                >
                  {hasEventDetails ? 'View Store' : 'View Store'}
                </button>
              )}
              <button
                onClick={handleShare}
                className="grid grid-cols-2 pl-1 pr-2 py-1 place-items-center rounded-md border border-white/30 bg-green-700/90 text-white hover:bg-green-800/90 transition-colors"
              >
                <Share2 className="size-4" />
                Share
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile & Tablet Cover Photo Banner */}
      <div className="relative lg:hidden min-h-60 sm:h-56 md:h-64 w-full overflow-hidden rounded-b-xl bg-gradient-to-r from-slate-200 to-slate-300">
        <img
          loading="lazy"
          src={bannerImage}
          onClick={() => navigate('/store')}
          alt={`${vendorInfo.shopName} ${hasEventBanner ? 'event banner' : 'cover'}`}
          className="h-full w-full object-cover absolute inset-0"
          onError={(e) => {
            e.target.src = '/images/bannernotfound.jpeg';
          }}
        />
        <div
          onClick={() => navigate(-1)}
          className="absolute top-0 left-0 right-0 flex px-4 gap-3 pt-2 pb-1 bg-black/40 items-center cursor-pointer z-10"
        >
          <button className="grid size-8 place-items-center rounded-full bg-white/80 backdrop-blur-sm text-slate-700 hover:bg-white transition-colors shadow-sm">
            <ArrowLeft className="size-4" />
          </button>
          <div className="flex gap-1 items-center text-sm font-bold text-white">
            <p>Home</p>
            <p>/</p>
            <p>Profile</p>
          </div>
        </div>
      </div>

      {/* Profile Section - Mobile */}
      <div className="relative lg:hidden px-4 pb-2">
        <div className="flex flex-col sm:flex-row sm:items-end sm:gap-4">
          <div className="-mt-16 sm:-mt-20 md:-mt-24 pointer-events-none z-10">
            <img
              loading="lazy"
              src={vendorInfo.profileImg}
              alt={`${vendorInfo.name} profile`}
              className="size-28 sm:size-32 md:size-40 rounded-full border-4 border-white bg-white object-cover shadow-lg"
              onError={(e) => {
                e.target.src = '/images/imagenotfound.webp';
              }}
            />
          </div>
          <div className="inline-block ml-auto -mt-12">
            <div className="flex items-center gap-2 mt-2 sm:mt-0">
              {!vendorInfo?.isShowcaseOnly && (
                <button
                  onClick={() => {
                    console.log(
                      `${URLS.USER}/vendor/profile?mobile=${vendorInfo?.phone?.replace('+91', '')}`
                    );
                    window.open(
                      `${URLS.USER}/vendor/profile?mobile=${vendorInfo?.phone?.replace('+91', '')}`
                    );
                  }}
                  className="px-4 py-1.5 bg-orange-600 text-white rounded-md font-semibold text-sm hover:bg-orange-700 transition-colors"
                >
                  {hasEventDetails ? 'View Store' : 'View Store'}
                </button>
              )}
              <button
                onClick={handleShare}
                className="grid grid-cols-2 pl-1 pr-2 py-1.5 place-items-center rounded-md border border-slate-300 bg-white text-slate-600 hover:bg-slate-50 hover:text-slate-700 transition-colors"
              >
                <Share2 className="size-4" />
                Share
              </button>
            </div>
          </div>
          <div className="flex-1 mt-3 sm:mt-0 sm:pb-2">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
              <div>
                <h1 className="text-xl sm:text-2xl font-bold tracking-tight text-slate-900">
                  {vendorInfo.shopName}
                </h1>
                {vendorInfo.subtitle && (
                  <p className="text-sm text-slate-500">{vendorInfo.subtitle}</p>
                )}
              </div>
            </div>
            {/* Social Media Links - Mobile */}
            <div className="flex items-center gap-1 mt-2">
              {vendorInfo.phone && (
                <ContactItem
                  icon={WhatsAppIcon}
                  color="text-green-600"
                  bgColor="bg-green-100"
                  value={vendorInfo.phone}
                  onClick={() => {
                    const phoneNumber = vendorInfo.phone?.replace(/[^0-9]/g, '');
                    window.open(`https://wa.me/${phoneNumber}`, '_blank');
                  }}
                  type="link"
                  isSvgComponent={true}
                />
              )}
              {vendorInfo.addressStr && (
                <ContactItem
                  icon={MapPin}
                  color="text-red-600"
                  bgColor="bg-red-100"
                  value={vendorInfo.addressStr}
                  vendor={vendorInfo}
                  onClick={() => {
                    const q =
                      vendorInfo?.attrMap?.latitude && vendorInfo?.attrMap?.longitude
                        ? `${vendorInfo.attrMap.latitude},${vendorInfo.attrMap.longitude}`
                        : vendorInfo?.addressStr;
                    if (q) {
                      const encodedQuery = encodeURIComponent(q);
                      const mapsUrl = `https://www.google.com/maps/search/?api=1&query=${encodedQuery}`;
                      window.open(mapsUrl, '_blank', 'noopener,noreferrer');
                    }
                  }}
                  type="link"
                  isSvgComponent={false}
                />
              )}
              {vendorInfo?.socialMedia?.map((link) => {
                const iconConfig = IconMap[link?.name];
                return (
                  <ContactItem
                    key={link?.id}
                    icon={iconConfig?.component}
                    color={iconConfig?.color}
                    bgColor=""
                    value={link?.value}
                    onClick={(value) => window.open(value)}
                    type="link"
                    isSvgComponent={link?.name !== 'website'}
                  />
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

const TabButton = forwardRef(({ id, activeTab, setActiveTab, label }, ref) => {
  const isActive = activeTab === id;
  return (
    <button
      ref={ref}
      onClick={() => setActiveTab(id)}
      className={`whitespace-nowrap border-b-2 px-1 py-1 text-sm font-bold transition-colors ${
        isActive
          ? 'border-emerald-500 text-emerald-600'
          : 'border-transparent text-slate-500 hover:border-slate-300 hover:text-slate-700'
      }`}
    >
      {label}
    </button>
  );
});

TabButton.displayName = 'TabButton';

function OverviewPanel({ info }) {
  return (
    <div className="grid gap-1 pt-4 md:grid-cols-3">
      <div className="md:col-span-2">
        <h3 className="mb-3 text-xl font-semibold text-slate-900">About Us</h3>
        <div
          className="bg-gray-50 rounded-md px-3 py-2 text-gray-700 text-sm
            [&_h1]:text-2xl [&_h1]:font-bold [&_h1]:mb-1 [&_h1]:text-gray-900
            [&_h2]:text-xl [&_h2]:font-bold [&_h2]:mb-1 [&_h2]:text-gray-900
            [&_h3]:text-lg [&_h3]:font-semibold [&_h3]:mb-1 [&_h3]:text-gray-900
            [&_p]:mb-1 [&_p]:leading-snug
            [&_ul]:list-disc [&_ul]:ml-6 [&_ul]:mb-3 [&_ul]:space-y-1x
            [&_ol]:list-decimal [&_ol]:ml-6 [&_ol]:mb-3 [&_ol]:space-y-1
            [&_li]:leading-snug
            [&_strong]:font-semibold [&_strong]:text-gray-900
            [&_em]:italic
            [&_a]:text-[#F77C06] [&_a]:underline [&_a]:hover:text-orange-600"
          dangerouslySetInnerHTML={{ __html: info.about }}
        />
      </div>
      <div>
        <h3 className="mb-3 text-xl font-semibold text-slate-900">Contact Info</h3>
        <div className="space-y-3">
          <ContactItem
            icon={Phone}
            label="Give us a call"
            value={info.phone}
            color="text-blue-600"
            bgColor="bg-blue-100"
            type="phone"
            onClick={(value) => window.open(`tel:${value}`)}
          />
          {info.alternatePhone && (
            <ContactItem
              icon={Phone}
              label="Alternate contact"
              value={info.alternatePhone}
              color="text-emerald-600"
              bgColor="bg-emerald-100"
              type="phone"
              onClick={(value) => window.open(`tel:${value}`)}
            />
          )}
          <ContactItem
            icon={MapPin}
            label="View On Map"
            value={info.addressStr}
            vendor={info}
            color="text-red-600"
            bgColor="bg-red-100"
            type="address"
            onClick={(vendor) => {
              const q =
                vendor?.detailedAddress?.latitude && vendor?.detailedAddress?.longitude
                  ? `${vendor.detailedAddress.latitude},${vendor.detailedAddress.longitude}`
                  : vendor?.addressStr;
              if (q) {
                const encodedQuery = encodeURIComponent(q);
                const mapsUrl = `https://www.google.com/maps/search/?api=1&query=${encodedQuery}`;
                window.open(mapsUrl, '_blank', 'noopener,noreferrer');
              }
            }}
          />
          <ContactItem
            icon={Mail}
            label="For Enquiry & Support"
            value={info.email}
            color="text-yellow-600"
            bgColor="bg-yellow-100"
            type="email"
            onClick={(value) => window.open(`mailto:${value}`)}
          />
        </div>
      </div>
    </div>
  );
}

function EventPanel({ info }) {
  const contentRef = useRef(null);
  const htmlContent = info.attrMap['eventDetails'];
  const eventBanner = info.attrMap['eventBanner'];

  useEffect(() => {
    if (contentRef.current) {
      const links = contentRef.current.querySelectorAll('a');
      links.forEach((link) => {
        link.setAttribute('target', '_blank');
        link.setAttribute('rel', 'noopener noreferrer');
        const href = link.getAttribute('href');
        if (
          href &&
          !href.startsWith('http') &&
          !href.startsWith('mailto:') &&
          !href.startsWith('/')
        ) {
          link.setAttribute('href', `https://${href}`);
        }
      });
    }
  }, [htmlContent]);

  return (
    <div className="pt-4">
      {/* Mobile: Banner on top */}
      <div className="w-full lg:hidden mb-3">
        <img
          src={eventBanner}
          alt="Event Banner"
          className="w-full rounded-lg object-cover"
          onError={(e) => {
            e.target.src = '/images/bannernotfound.jpeg';
          }}
        />
      </div>
      {/* Desktop: Side by side layout */}
      <div className="flex flex-col lg:flex-row gap-6">
        <div className="flex-1">
          <h3 className="mb-3 text-xl font-semibold text-slate-900">About The Event</h3>
          <div
            ref={contentRef}
            className="bg-gray-50 rounded-md px-3 py-1 text-gray-700 text-sm
              [&_h1]:text-2xl [&_h1]:font-bold [&_h1]:mb-1 [&_h1]:text-gray-900
              [&_h2]:text-xl [&_h2]:font-bold [&_h2]:mb-0 [&_h2]:text-gray-900
              [&_h3]:text-lg [&_h3]:font-semibold [&_h3]:mb-1 [&_h3]:text-gray-900
              [&_p]:mb-0 [&_p]:leading-snug
              [&_ul]:list-disc [&_ul]:ml-6 [&_ul]:mb-0 [&_ul]:space-y-1x
              [&_ol]:list-decimal [&_ol]:ml-6 [&_ol]:mb-0 [&_ol]:space-y-1
              [&_li]:leading-snug
              [&_strong]:font-semibold [&_strong]:text-gray-900
              [&_em]:italic
              [&_a]:text-[#F77C06] [&_a]:underline [&_a]:hover:text-orange-600"
            dangerouslySetInnerHTML={{ __html: info.attrMap['eventDetails'] }}
          />
        </div>
        {eventBanner && (
          <div className="hidden lg:block lg:w-1/3">
            <img
              src={eventBanner}
              alt="Event Banner"
              className="w-full rounded-lg object-cover sticky top-4"
              onError={(e) => {
                e.target.src = '/images/imagenotfound.webp';
              }}
            />
          </div>
        )}
      </div>
    </div>
  );
}

function AnnouncementPanel({ info }) {
  const contentRef = useRef(null);
  const htmlContent = info.attrMap['announcement'];

  useEffect(() => {
    if (contentRef.current) {
      const links = contentRef.current.querySelectorAll('a');
      links.forEach((link) => {
        link.setAttribute('target', '_blank');
        link.setAttribute('rel', 'noopener noreferrer');
        const href = link.getAttribute('href');
        if (
          href &&
          !href.startsWith('http') &&
          !href.startsWith('mailto:') &&
          !href.startsWith('/')
        ) {
          link.setAttribute('href', `https://${href}`);
        }
      });
    }
  }, [htmlContent]);

  return (
    <div className="pt-4">
      {/* Desktop: Side by side layout */}
      <div className="flex flex-col lg:flex-row gap-6">
        <div className="flex-1">
          <div
            ref={contentRef}
            className="bg-gray-50 rounded-md px-3 py-1 text-gray-700 text-sm
              [&_h1]:text-2xl [&_h1]:font-bold [&_h1]:mb-1 [&_h1]:text-gray-900
              [&_h2]:text-xl [&_h2]:font-bold [&_h2]:mb-0 [&_h2]:text-gray-900
              [&_h3]:text-lg [&_h3]:font-semibold [&_h3]:mb-1 [&_h3]:text-gray-900
              [&_p]:mb-0 [&_p]:leading-snug
              [&_ul]:list-disc [&_ul]:ml-6 [&_ul]:mb-0 [&_ul]:space-y-1x
              [&_ol]:list-decimal [&_ol]:ml-6 [&_ol]:mb-0 [&_ol]:space-y-1
              [&_li]:leading-snug
              [&_strong]:font-semibold [&_strong]:text-gray-900
              [&_em]:italic
              [&_a]:text-[#F77C06] [&_a]:underline [&_a]:hover:text-orange-600"
            dangerouslySetInnerHTML={{ __html: info.attrMap['announcement'] }}
          />
        </div>
      </div>
    </div>
  );
}

function ContactItem({
  icon: Icon,
  label,
  value,
  color = '',
  bgColor = 'bg-slate-100',
  vendor = null,
  onClick = null,
  type = 'default',
  isSvgComponent = false,
}) {
  if (!value) return null;
  const iconColor = color || 'text-slate-500';

  return (
    <div
      onClick={() => {
        switch (type) {
          case 'link':
            onClick(value);
            break;
          case 'address':
            onClick(vendor);
            break;
          case 'phone':
            onClick(value);
            break;
          case 'email':
            onClick(value);
            break;
          default:
            break;
        }
      }}
      className={`flex items-center gap-3 ${type === 'link' && 'cursor-pointer'}`}
    >
      <div
        className={`mt-1 min-w-7 grid size-7 place-items-center rounded-full ${bgColor} ${iconColor}`}
      >
        {isSvgComponent ? <Icon /> : <Icon className="size-4" />}
      </div>
      <div>
        <div
          className={`text-slate-800 ${type === 'address' ? 'font-normal text-xs' : 'font-medium text-sm'}`}
        >
          {type === 'link' ? label : value}
        </div>
        {type !== 'link' && <div className="text-xs text-slate-500">{label}</div>}
      </div>
    </div>
  );
}

function GalleryPanel({ images, isLoading, onImageClick }) {
  if (isLoading) {
    return (
      <div>
        <div className="columns-2 mt-4 gap-4 sm:columns-3 lg:columns-4">
          {Array.from({ length: 8 }).map((_, index) => (
            <SkeletonCard key={index} />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="columns-2 mt-4 gap-4 sm:columns-3 lg:columns-4">
        {images.map((src, idx) => (
          <div key={idx} className="mb-4 break-inside-avoid" onClick={() => onImageClick(src)}>
            <img
              loading="lazy"
              src={src || '/images/imagenotfound.webp'}
              alt={`Gallery image ${idx + 1}`}
              className="w-full rounded-lg object-cover ring-1 ring-slate-200"
              onError={(e) => {
                e.target.src = '/images/imagenotfound.webp';
              }}
            />
          </div>
        ))}
      </div>
    </div>
  );
}
